import { useContext } from "react";
import { PartnerCtx } from "@/contexts/PartnerContextProvider";
import { usePartnerContextQuery } from "./usePartnerContextQuery";

export type {
  PartnerCompany,
  TenantMembership,
  EffectiveCapabilities,
  PortalPreferences,
  TenantInvitation,
  PartnerContext,
  UsePartnerContextReturn,
} from "./usePartnerContextQuery";

import type { UsePartnerContextReturn } from "./usePartnerContextQuery";

export {
  useSwitchPartnerTenant,
  usePartnerContextQuery,
} from "./usePartnerContextQuery";

/**
 * Pack 9.A: read the partner context from the React Context provider when
 * the caller is rendered inside `PartnerLayout` (which wires the provider).
 * Falls back to a direct React-Query fetch when used outside the provider
 * (e.g. worker-shell embeds, modals rendered above the partner shell).
 *
 * The Provider materializes the context exactly once for the whole partner
 * shell, so every page (Dashboard, Workers, Verifications, Agreements,
 * Governance, Commercial …) reads the SAME company / tenant / capability
 * map without re-fetching or risking divergent capability values.
 */
export function usePartnerContext(): UsePartnerContextReturn {
  const ctxValue = useContext(PartnerCtx);
  // When inside the provider, the React-Query call below is gated to never run
  // (enabled: false) so we don't double-fetch. When outside the provider this
  // hook becomes the actual fetcher.
  const fallbackValue = usePartnerContextQuery({ enabled: ctxValue === null });
  return ctxValue ?? fallbackValue;
}
